import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { 
  faArrowLeft, 
  faLink, 
  faUnlink, 
  faMapMarkerAlt, 
  faHistory,
  faUserShield,
  faChild,
  faCalendar,
  faCheckCircle,
  faTimesCircle,
  faEye
} from "@fortawesome/free-solid-svg-icons";

import { DashboardLayout } from "../../Components/Layout/DashboardLayout";
import CustomButton from "../../Components/CustomButton";
import CustomModal from "../../Components/CustomModal";
import Loader from '../../Components/Loader';

import "./style.css";

import { 
  useGetFamilyLinksQuery, 
  useUpdateFamilyLinkMutation,
  useGetAuditLogQuery,
  useGetLocationHistoryQuery
} from "../../Redux/Apis/Family";
import { dateFormatter } from "../../Utils";
import { placeholderImage } from "../../Assets/images";

const getActionIcon = (action) => {
  switch (action) {
    case 'created': return faLink;
    case 'activated': return faCheckCircle;
    case 'revoked': return faTimesCircle;
    case 'location_viewed': return faEye;
    case 'location_requested': return faMapMarkerAlt;
    default: return faHistory;
  }
};

const getActionColor = (action) => {
  switch (action) {
    case 'created': return '#0d6efd';
    case 'activated': return '#28a745';
    case 'revoked': return '#dc3545';
    case 'location_viewed': return '#6c757d';
    case 'location_requested': return '#17a2b8';
    default: return '#6c757d';
  }
};

export const FamilyLinkDetails = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [updateFamilyLink, { isLoading: isUpdating }] = useUpdateFamilyLinkMutation();

  const [showRevokeModal, setShowRevokeModal] = useState(false);
  const [showActivateModal, setShowActivateModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  // Fetch the specific link
  const { data: linksData, isLoading, refetch } = useGetFamilyLinksQuery({
    page: 1,
    limit: 100,
  });

  // Find the specific link from the list
  const link = linksData?.data?.find(l => l._id === id);

  // Fetch audit log
  const { data: auditData, isLoading: loadingAudit } = useGetAuditLogQuery({ id }, { skip: !id });

  // Fetch location history (last 24 hours)
  const { data: locationData, isLoading: loadingLocation } = useGetLocationHistoryQuery({ id, hours: 24 }, { skip: !id || !link?.childId });

  useEffect(() => {
    document.title = 'The Turn At | Marshall Details';
  }, []);

  const handleRevoke = async () => {
    await updateFamilyLink({ id, status: 'revoked' });
    refetch();
    setShowRevokeModal(false);
    setSuccessMessage('Connection has been revoked');
    setShowSuccessModal(true);
  };

  const handleActivate = async () => {
    await updateFamilyLink({ id, status: 'active' });
    refetch();
    setShowActivateModal(false);
    setSuccessMessage('Connection has been restored');
    setShowSuccessModal(true);
  };

  const getStatusBadge = (status) => {
    const statusClasses = {
      active: 'badge-success',
      pending: 'badge-warning',
      revoked: 'badge-danger',
    };
    return (
      <span className={`badge ${statusClasses[status] || 'badge-secondary'}`}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  if (isLoading) {
    return (
      <DashboardLayout>
        <Loader />
      </DashboardLayout>
    );
  }

  if (!link) {
    return (
      <DashboardLayout>
        <div className="container-fluid">
          <div className="row">
            <div className="col-12">
              <div className="dashCard">
                <h2>Connection Not Found</h2>
                <p>The requested Marshall connection could not be found.</p>
                <CustomButton text="Go Back" onClick={() => navigate(-1)} />
              </div>
            </div>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <>
      <DashboardLayout>
        <div className="container-fluid">
          <div className="row mb-3">
            <div className="col-12">
              <button className="backBtn" onClick={() => navigate(-1)}>
                <FontAwesomeIcon icon={faArrowLeft} /> Back to Marshall
              </button>
            </div>
          </div>

          <div className="row mb-3">
            <div className="col-12">
              <div className="dashCard">
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <h2 className="mainTitle mb-0">Marshall Connection Details</h2>
                  <div className="headerActions">
                    {link.status === 'active' && (
                      <CustomButton 
                        type="button"
                        text="Revoke"
                        className="dangerButton"
                        icon={faUnlink}
                        onClick={() => setShowRevokeModal(true)}
                      />
                    )}
                    {link.status === 'revoked' && (
                      <CustomButton 
                        type="button"
                        text="Restore"
                        className="successButton"
                        icon={faLink}
                        onClick={() => setShowActivateModal(true)}
                      />
                    )}
                  </div>
                </div>

                <div className="row">
                  {/* Link Info */}
                  <div className="col-md-6 mb-4">
                    <div className="detailsCard">
                      <h4><FontAwesomeIcon icon={faLink} className="me-2" /> Connection Info</h4>
                      <div className="detailRow">
                        <span className="detailLabel">Status</span>
                        <span className="detailValue">{getStatusBadge(link.status)}</span>
                      </div>
                      <div className="detailRow">
                        <span className="detailLabel">Connected Via</span>
                        <span className="detailValue">
                          <span className={`linkedByBadge ${link.linkedBy === 'admin' ? 'admin' : 'qr'}`}>
                            {link.linkedBy === 'admin' ? 'Admin' : 'QR Scan'}
                          </span>
                        </span>
                      </div>
                      <div className="detailRow">
                        <span className="detailLabel">Created</span>
                        <span className="detailValue">{dateFormatter(link.createdAt)}</span>
                      </div>
                      {link.linkedAt && (
                        <div className="detailRow">
                          <span className="detailLabel">Linked At</span>
                          <span className="detailValue">{dateFormatter(link.linkedAt)}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Guardian Info */}
                  <div className="col-md-6 mb-4">
                    <div className="detailsCard">
                      <h4><FontAwesomeIcon icon={faUserShield} className="me-2" /> Guardian</h4>
                      {link.parentId ? (
                        <div className="userProfile">
                          <img 
                            src={link.parentId.picture ? `${process.env.REACT_APP_IMAGE_ENDPOINT}${link.parentId.picture}` : placeholderImage}
                            alt={link.parentId.name}
                            className="profileImage"
                          />
                          <div className="profileInfo">
                            <div className="profileName">{link.parentId.name}</div>
                            <div className="profileEmail">{link.parentId.email}</div>
                          </div>
                        </div>
                      ) : (
                        <p className="text-muted">Guardian information not available</p>
                      )}
                    </div>
                  </div>

                  {/* Member Info */}
                  <div className="col-md-6 mb-4">
                    <div className="detailsCard">
                      <h4><FontAwesomeIcon icon={faChild} className="me-2" /> Member</h4>
                      {link.childId ? (
                        <div className="userProfile">
                          <img 
                            src={link.childId.picture ? `${process.env.REACT_APP_IMAGE_ENDPOINT}${link.childId.picture}` : placeholderImage}
                            alt={link.childId.name}
                            className="profileImage"
                          />
                          <div className="profileInfo">
                            <div className="profileName">{link.childId.name}</div>
                            <div className="profileEmail">{link.childId.email}</div>
                          </div>
                        </div>
                      ) : (
                        <p className="text-muted">Waiting for member to join...</p>
                      )}
                    </div>
                  </div>

                  {/* Recent Location */}
                  {link.childId && locationData?.data?.length > 0 && (
                    <div className="col-md-6 mb-4">
                      <div className="detailsCard">
                        <h4><FontAwesomeIcon icon={faMapMarkerAlt} className="me-2" /> Last Known Location</h4>
                        {loadingLocation ? (
                          <p>Loading...</p>
                        ) : (
                          <div className="locationInfo">
                            <div className="locationCoords">
                              {locationData.data[0].latitude.toFixed(6)}, {locationData.data[0].longitude.toFixed(6)}
                            </div>
                            <div className="locationTime">
                              {dateFormatter(locationData.data[0].timestamp)}
                            </div>
                            {locationData.data[0].accuracy && (
                              <div className="locationAccuracy">
                                Accuracy: ±{Math.round(locationData.data[0].accuracy)}m
                              </div>
                            )}
                          </div>
                        )}
                        <CustomButton 
                          text="View Location History"
                          className="secondaryButton mt-3"
                          onClick={() => navigate(`/family-management/location/${id}`)}
                        />
                      </div>
                    </div>
                  )}
                </div>

                {/* Audit Log */}
                <div className="row">
                  <div className="col-12">
                    <div className="detailsCard">
                      <h4><FontAwesomeIcon icon={faHistory} className="me-2" /> Activity Log</h4>
                      {loadingAudit ? (
                        <p>Loading audit log...</p>
                      ) : auditData?.data?.length > 0 ? (
                        <div className="auditLog">
                          {auditData.data.map((audit, index) => (
                            <div key={audit._id || index} className="auditItem">
                              <div 
                                className="auditIcon"
                                style={{ backgroundColor: `${getActionColor(audit.action)}20`, color: getActionColor(audit.action) }}
                              >
                                <FontAwesomeIcon icon={getActionIcon(audit.action)} />
                              </div>
                              <div className="auditContent">
                                <div className="auditAction">
                                  {audit.action.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                                </div>
                                {audit.details && (
                                  <div className="auditDetails">{audit.details}</div>
                                )}
                                <div className="auditMeta">
                                  {audit.performedBy?.name || audit.performedByRole} • {dateFormatter(audit.createdAt)}
                                  {audit.ipAddress && ` • ${audit.ipAddress}`}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-muted">No activity recorded yet</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Modals */}
        <CustomModal 
          loading={isUpdating} 
          show={showRevokeModal} 
          close={() => setShowRevokeModal(false)} 
          action={handleRevoke} 
          heading='Are you sure you want to revoke this Marshall connection?' 
        />
        
        <CustomModal 
          loading={isUpdating} 
          show={showActivateModal} 
          close={() => setShowActivateModal(false)} 
          action={handleActivate} 
          heading='Are you sure you want to restore this Marshall connection?' 
        />
        
        <CustomModal 
          show={showSuccessModal} 
          close={() => setShowSuccessModal(false)} 
          success 
          heading={successMessage} 
        />
      </DashboardLayout>

      <style jsx>{`
        .backBtn {
          background: none;
          border: none;
          color: #28a745;
          font-size: 14px;
          cursor: pointer;
          padding: 8px 0;
        }
        .backBtn:hover {
          text-decoration: underline;
        }
        .headerActions {
          display: flex;
          gap: 12px;
        }
        .dangerButton {
          background-color: #dc3545 !important;
          border-color: #dc3545 !important;
        }
        .successButton {
          background-color: #28a745 !important;
          border-color: #28a745 !important;
        }
        .userProfile {
          display: flex;
          align-items: center;
          gap: 16px;
        }
        .profileImage {
          width: 64px;
          height: 64px;
          border-radius: 50%;
          object-fit: cover;
        }
        .profileName {
          font-size: 18px;
          font-weight: 600;
          color: #333;
        }
        .profileEmail {
          font-size: 14px;
          color: #666;
        }
        .locationInfo {
          padding: 16px;
          background: #f8f9fa;
          border-radius: 8px;
        }
        .locationAccuracy {
          font-size: 13px;
          color: #666;
          margin-top: 8px;
        }
      `}</style>
    </>
  );
};

export default FamilyLinkDetails;

